/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import DisclaimerFooter from "components/DisclaimerFooter"
import { useEffect } from "react"
import { pageMetaData, updateMetaTags } from "utils/metaTags"

const Privacy = () => {
  // Update meta tags for privacy page
  useEffect(() => {
    const { title, description, keywords } = pageMetaData.privacy
    updateMetaTags(title, description, keywords)
  }, [])
  return (
    <div className="pt-1 pb-3">
      <h1 className="fs-4 pb-3">Privacy Policy</h1>

      <p>
        <strong>Last Updated:</strong> August 20, 2026
      </p>

      <h2 className="fs-5 mt-4 mb-3">Introduction</h2>
      {/* AIDEV-NOTE: #522 - the operator is named so that "we" has an antecedent: a reader
          cannot otherwise tell who holds their information or who they are contacting.
          "Safe Retirement Spending" is not a legal person, and the 2026-08-20 decision declined
          to form an LLC. This discloses nothing new - About.tsx already names the same person
          publicly - and no postal address is given, per the 2026-08-15 decision.
          Deliberately NOT phrased as "data controller". That is GDPR vocabulary, and GDPR's
          application here is doubtful: Art 3(2) reaches a US operator only on the targeting or
          monitoring tests, and Recital 23 says mere accessibility of a site is not targeting.
          Importing the term would imply a compliance posture nobody decided to adopt. Naming a
          contactable person carries the useful part without the claim. */}
      <p>
        Safe Retirement Spending (&quot;we&quot;, &quot;our&quot;, or &quot;us&quot;) is committed
        to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and
        safeguard your information when you visit our website saferetirementspending.com (the
        &quot;Site&quot;) or use our retirement calculator and related services (collectively, the
        &quot;Services&quot;).
      </p>
      <p>
        The Site is operated by John Firth, who is responsible for the information described in this
        Policy. You can reach us using the contact details at the end of this Policy.
      </p>
      <p>
        Please read this Privacy Policy carefully. By accessing or using our Services, you
        acknowledge that you have read, understood, and agree to be bound by this Privacy Policy. If
        you do not agree with our policies, please do not access or use our Services.
      </p>

      <h2 className="fs-5 mt-4 mb-3">Information We Collect</h2>
      <p>We collect information that you voluntarily provide when using our Services:</p>
      <ul>
        <li>
          <strong>Calculator Inputs:</strong> Financial data you enter into our retirement
          calculator, including portfolio values, retirement dates, asset allocations, and expected
          cash flows.{" "}
          <em>
            As detailed in our Data Storage section below, these inputs are not permanently stored
            after your session is complete.
          </em>
        </li>
        <li>
          <strong>Communications:</strong> If you contact us directly, we may receive additional
          information about you, such as your name, email address, the contents of your message, and
          any other information you choose to provide.
        </li>
      </ul>
      <p>We also automatically collect certain basic information when you visit our Site:</p>
      <ul>
        <li>
          <strong>Device Data:</strong> Technical information about your device, including IP
          address, browser type, operating system, and other technical information.
        </li>
        <li>
          <strong>Usage Data:</strong> Information about how you use our Site, such as the pages you
          visit, the time and date of your visits, and other usage patterns.
        </li>
        <li>
          <strong>Cookies:</strong> We do not use cookies. See the Cookies and Advertising section
          below.
        </li>
        <li>
          <strong>Error and Performance Data:</strong> Technical information collected when an error
          occurs or for a sample of page loads, as described in the Error Tracking and Performance
          Monitoring section below.
        </li>
      </ul>

      <h2 className="fs-5 mt-4 mb-3">How We Use Your Information</h2>
      <p>We use the information we collect for the following purposes:</p>
      <ul>
        <li>To provide, maintain, and improve our Services</li>
        <li>To respond to your inquiries and fulfill your requests</li>
        <li>To monitor and analyze usage patterns and trends</li>
        <li>To protect the security and integrity of our Services</li>
        <li>To comply with applicable laws, regulations, or legal processes</li>
      </ul>
      <p>
        We do not use the financial data you input into our calculator for marketing purposes, nor
        do we sell this information to third parties.
      </p>

      <h2 className="fs-5 mt-4 mb-3">Data Storage and Security</h2>
      <p>
        Calculator inputs are not stored in a database, and we do not retain your results after your
        calculation completes. Our servers do write operational logs (request metadata and, for
        errors, diagnostic details) as part of running the Service. These logs are kept only for a
        short period, for security and operational purposes, and are not used to build any profile
        of you. We take measures to limit what appears in these logs and to restrict who can access
        them, and we implement technical and organizational measures designed to protect any
        information transmitted to us against unauthorized access, loss, or damage.
      </p>
      {/* AIDEV-NOTE: #916 - the retention wording here is deliberately unnumbered. The
          previous text promised "automatically deleted after 7 days", which is true of
          Loki (loki/loki-config.yml, retention_period: 168h) but not demonstrably true of
          the other two paths the same lines reach: Docker's `local` driver rotates the
          proxy container's logs by SIZE, not time, and the proxy-logs volume's contents
          are unverified on the live host. See #924. A privacy statement the code
          contradicts is worse than a vague one, so the number waits for #923 (truncating
          client IPs at write time, after which these logs hold no personal data) or #924
          (bringing every path under a stated period). Put a figure back only once one of
          those lands, and make it the figure actually enforced.
          #945: the same promise appeared a SECOND time, under Your Privacy Rights, and #916
          removed only this one. If a figure ever goes back, it goes back in both places or
          neither - that is what the retention pattern in utils/legalCopy.test.ts enforces. */}

      <h2 className="fs-5 mt-4 mb-3">Cookies and Advertising</h2>
      <p>
        <strong>We do not use cookies, and we do not show advertising.</strong> This Site sets no
        cookies of its own and loads no advertising, analytics or tracking scripts from any third
        party. Because nothing is stored on your device and no third party is measuring your visit,
        there is no consent banner here and nothing for you to opt out of.
      </p>
      <p>
        This was not always the case. Until August 2026 the Site loaded Google Tag Manager, Google
        Analytics and Google AdSense, together with a Cookiebot consent banner that gated them. All
        four have been removed, along with the cookies they set (<code>_ga</code>, <code>_gid</code>
        , <code>_gat</code>, the AdSense cookies and Cookiebot&apos;s own <code>CookieConsent</code>
        ). Any of those cookies still on your device is a leftover from a previous visit and is no
        longer set or read by us; you can delete them through your browser&apos;s settings at any
        time.
      </p>
      <p>
        We do not run analytics of any kind in their place. We do not sell or share personal
        information, and we do not permit any third party to use this Site to track you across other
        websites.
      </p>

      <h2 className="fs-5 mt-4 mb-3">Error Tracking and Performance Monitoring</h2>
      {/* AIDEV-NOTE: #959 - "Sentry does not collect your IP address" is guaranteed by an
          org-level Sentry setting ("Prevent Storing of IP Addresses", enabled 2026-08-18), not
          by anything in this repo. Both SDK clients already declined to send one and it was
          stored anyway: the address is attached server-side at ingest, so sentry.ts's
          beforeSend deletion cannot achieve this and never could. Since @sentry/react 11 the
          browser client also asks Sentry not to infer it (`dataCollection.userInfo: false`
          sends `infer_ip: "never"`), but whether ingest honours that is untested here, so the
          org setting is still what guarantees it. If that setting is ever turned off, this
          sentence is no longer guaranteed, with no diff and no failing test to catch it.
          Scoped to Sentry deliberately. A site-wide "we do not collect your IP address" would
          contradict Your Privacy Rights below, where operational logs legitimately hold IP
          addresses (#923, #924 both open). */}
      <p>
        We use Sentry, an error-tracking and performance-monitoring service, to help us detect and
        diagnose problems with our Site. When an error occurs, or for a sample of page loads used
        for performance monitoring, Sentry may collect technical information such as your browser
        and device information, the page you were on, and a trace of the actions leading up to the
        error. Sentry does not collect your IP address. If you choose to click &quot;Report
        Feedback&quot; after seeing an error message, you can optionally submit your name, email
        address, and comments, which are attached to that error report. This information is used
        solely to maintain and improve the reliability of our Service and is not used for
        advertising. Sentry&apos;s data practices are described in{" "}
        <a
          href="https://sentry.io/privacy/"
          target="_blank"
          rel="noopener noreferrer"
          className="fw-bold"
        >
          Sentry&apos;s own privacy policy
        </a>
        .
      </p>

      <h2 className="fs-5 mt-4 mb-3">Your Privacy Rights</h2>
      {/* AIDEV-NOTE: #945 - this paragraph is the SECOND place the "7 days" figure lived, and
          the one #916 missed. Do not reintroduce a retention period here: the reasons are in
          the note under Data Storage and Security above, and they apply identically. Note also
          that this no longer says privacy rights "are not applicable" - operational logs hold
          IP addresses, which are personal data, so rights are limited in what they can return
          rather than absent. Guarded by utils/legalCopy.test.ts. */}
      <p>
        We do not store calculator inputs or results in a database, so there is no saved record of
        your calculations for us to provide, correct, or delete. Our operational logs are kept only
        for a short period and are not used to build a profile of you, as described under Data
        Storage and Security above. This limits what a request for access, correction or deletion
        can practically return; it does not remove your rights. If you want to know what we hold
        about you, contact us using the details below.
      </p>
      <p>In practice, you can:</p>
      <ul>
        <li>Contact us with any questions about our data practices</li>
        <li>Request information about the types of anonymous usage data we may collect</li>
        <li>Ask about our security practices for any data temporarily processed on our servers</li>
      </ul>
      <p>
        If you contact us via email, we will retain that communication only as long as needed to
        respond to your inquiry. You may request that we delete this correspondence at any time.
      </p>

      <h2 className="fs-5 mt-4 mb-3">Children&apos;s Privacy</h2>
      <p>
        Our Services are not intended for individuals under the age of 18. We do not knowingly
        collect personal information from children under 18. If you are a parent or guardian and
        believe that your child has provided us with personal information, please contact us so that
        we can delete the information.
      </p>

      <h2 className="fs-5 mt-4 mb-3">Updates to This Privacy Policy</h2>
      <p>
        We may update this Privacy Policy from time to time in response to changing legal,
        technical, or business developments. When we update our Privacy Policy, we will take
        appropriate measures to inform you, consistent with the significance of the changes we make.
        The date at the top of this Privacy Policy indicates when it was last updated.
      </p>

      <h2 className="fs-5 mt-4 mb-3">Contact Us</h2>
      <p>
        If you have any questions or concerns about this Privacy Policy or our privacy practices,
        please contact us at:{" "}
        <a href="mailto:support&#64;saferetirementspending&#46;com">
          support&#64;saferetirementspending&#46;com
        </a>
      </p>

      <DisclaimerFooter />
    </div>
  )
}

export default Privacy
